import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import PatientStatusAction, { PatientInactiveNotice } from '../components/PatientStatusControls';
import { 
  ArrowLeft,
  User,
  Calendar,
  Phone,
  Mail,
  MapPin,
  FileText,
  Image,
  Smile,
  Brain,
  Activity,
  DollarSign,
  Clock,
  Package,
  Download,
  Eye,
  Plus,
  Edit,
  AlertTriangle,
  CheckCircle,
  Trash2,
  ExternalLink,
  Camera,
  MessageSquare,
  Heart,
  Shield,
  Pill,
  Star,
  CreditCard,
  Receipt
} from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const PatientRecordPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [patient, setPatient] = useState(null);
  const [patientData, setPatientData] = useState({
    images: [],
    anamnese: [],
    odontogram: null,
    analyses: [],
    treatments: [],
    appointments: [],
    invoices: [],
    payments: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const tabs = [
    { id: 'overview', name: 'Visão Geral', icon: User },
    { id: 'treatments', name: 'Evolução Clínica', icon: Activity },
    { id: 'appointments', name: 'Agendamentos', icon: Calendar },
    { id: 'images', name: 'Imagens', icon: Image },
    { id: 'anamnese', name: 'Anamnese', icon: FileText },
    { id: 'odontogram', name: 'Odontograma', icon: Smile },
    { id: 'analyses', name: 'Análises IA', icon: Brain },
    { id: 'financial', name: 'Financeiro', icon: DollarSign }
  ];

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Buscar dados do paciente
      const patientResponse = await axios.get(`${API_URL}/patients/${patientId}`, { headers });
      setPatient(patientResponse.data);

      // Buscar todos os dados relacionados em paralelo
      const [
        imagesResponse,
        anamneseResponse,
        odontogramResponse,
        analysesResponse,
        treatmentsResponse,
        appointmentsResponse,
        invoicesResponse,
        paymentsResponse
      ] = await Promise.allSettled([
        axios.get(`${API_URL}/images/search?patient_id=${patientId}`, { headers }),
        axios.get(`${API_URL}/anamnese/responses`, { headers }),
        axios.get(`${API_URL}/odontograma/patient/${patientId}`, { headers }),
        axios.get(`${API_URL}/ai/analyses/${patientId}`, { headers }),
        axios.get(`${API_URL}/treatments?patient_id=${patientId}`, { headers }),
        axios.get(`${API_URL}/appointments?patient_id=${patientId}`, { headers }),
        axios.get(`${API_URL}/billing/invoices?patient_id=${patientId}`, { headers }),
        axios.get(`${API_URL}/billing/payments?patient_id=${patientId}`, { headers })
      ]);

      const images = imagesResponse.status === 'fulfilled' ? imagesResponse.value.data : [];
      const allAnamnese = anamneseResponse.status === 'fulfilled' ? anamneseResponse.value.data : [];
      const odontogram = odontogramResponse.status === 'fulfilled' ? odontogramResponse.value.data : null;
      const analyses = analysesResponse.status === 'fulfilled' ? analysesResponse.value.data : [];
      const treatments = treatmentsResponse.status === 'fulfilled' ? treatmentsResponse.value.data : [];
      const appointments = appointmentsResponse.status === 'fulfilled' ? appointmentsResponse.value.data : [];
      const invoices = invoicesResponse.status === 'fulfilled' ? invoicesResponse.value.data : [];
      const payments = paymentsResponse.status === 'fulfilled' ? paymentsResponse.value.data : [];

      // Filtrar anamnese para o paciente específico
      const patientAnamnese = allAnamnese.filter(item => 
        item.patient_name === patientResponse.data.full_name
      );

      setPatientData({
        images: images || [],
        anamnese: patientAnamnese || [],
        odontogram: odontogram,
        analyses: analyses || [],
        treatments: treatments || [],
        appointments: appointments || [],
        invoices: invoices || [],
        payments: payments || []
      });

    } catch (error) {
      console.error('Erro ao carregar dados do paciente:', error);
      toast?.error('Erro ao carregar dados do paciente');
      navigate('/ehr-visual');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    try {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return `${age} anos`;
    } catch {
      return 'N/A';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Planejado': 'bg-blue-100 text-blue-800',
      'Em Andamento': 'bg-yellow-100 text-yellow-800',
      'Concluído': 'bg-green-100 text-green-800',
      'Cancelado': 'bg-red-100 text-red-800',
      'Agendado': 'bg-blue-100 text-blue-800',
      'Confirmado': 'bg-green-100 text-green-800',
      'Realizado': 'bg-green-100 text-green-800',
      'Não Compareceu': 'bg-orange-100 text-orange-800',
      'pendente': 'bg-yellow-100 text-yellow-800',
      'pago': 'bg-green-100 text-green-800',
      'vencido': 'bg-red-100 text-red-800',
      'parcialmente_pago': 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const viewImage = async (image) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/patients/${image.patient_id}/images/${image.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        setSelectedImage(response.data);
        setShowImageModal(true);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
      toast?.error('Erro ao carregar imagem');
    }
  };

  const downloadImage = (image) => {
    if (image.image_data) {
      const link = document.createElement('a');
      link.href = `data:${image.mime_type};base64,${image.image_data}`;
      link.download = image.original_filename || `imagem_${image.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getTotalTreatmentCost = () => {
    return patientData.treatments.reduce((total, treatment) => total + (treatment.cost || 0), 0);
  };

  const getTotalInvoiceAmount = () => {
    return patientData.invoices
      .filter((invoice) => invoice.status !== 'cancelado')
      .reduce((total, invoice) => total + (invoice.total_amount || 0), 0);
  };

  const getTotalPaidAmount = () => {
    return patientData.payments
      .filter((payment) => payment.status === 'confirmado')
      .reduce((total, payment) => total + (payment.amount || 0), 0);
  };

  const getEvolutionFeed = () => {
    let feed = [];

    // Add manual treatments
    patientData.treatments.forEach(t => {
      feed.push({
        id: `t_${t.id}`,
        date: t.created_at,
        type: 'treatment',
        title: t.procedure,
        description: t.description,
        status: t.status,
        cost: t.cost,
        icon: Activity,
        iconBg: 'bg-purple-100 text-purple-600'
      });
    });

    // Add odontogram procedures
    if (patientData.odontogram && patientData.odontogram.teeth) {
      patientData.odontogram.teeth.forEach(tooth => {
        // procedures
        (tooth.procedures || []).forEach((proc, idx) => {
          feed.push({
            id: `odon_p_${tooth.tooth_number}_${idx}`,
            date: tooth.last_updated || patientData.odontogram.updated_at || new Date(),
            type: 'odontogram_procedure',
            title: `Realizado (Dente ${tooth.tooth_number}): ${proc}`,
            description: tooth.notes || 'Registrado no Odontograma Interativo.',
            status: 'Realizado',
            icon: Smile,
            iconBg: 'bg-green-100 text-green-600'
          });
        });
        
        // planned_procedures
        (tooth.planned_procedures || []).forEach((proc, idx) => {
          feed.push({
            id: `odon_pl_${tooth.tooth_number}_${idx}`,
            date: tooth.last_updated || patientData.odontogram.updated_at || new Date(),
            type: 'odontogram_planned',
            title: `Planejado (Dente ${tooth.tooth_number}): ${proc}`,
            description: tooth.notes || 'Registrado no Odontograma Interativo.',
            status: 'Planejado',
            icon: Smile,
            iconBg: 'bg-blue-100 text-blue-600'
          });
        });
      });
    }

    // Sort by date descending
    return feed.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Paciente não encontrado
          </h3>
          <button
            onClick={() => navigate('/ehr-visual')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Voltar para EHR Visual
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/ehr-visual')}
                className="mr-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                  <User className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{patient.full_name}</h1>
                  <p className="text-gray-600">
                    Prontuário Eletrônico Completo • {calculateAge(patient.birth_date)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/pacientes/${patientId}/edit`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar Paciente
              </button>
            </div>
          </div>
        </div>

        <PatientInactiveNotice patient={patient} />
        <PatientStatusAction key={patient.id} patient={patient} onChanged={setPatient} />
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <Activity className="w-6 h-6 text-purple-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Tratamentos</p>
                <p className="text-lg font-bold text-purple-600">{patientData.treatments.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <Calendar className="w-6 h-6 text-blue-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Consultas</p>
                <p className="text-lg font-bold text-blue-600">{patientData.appointments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <Image className="w-6 h-6 text-green-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Imagens</p>
                <p className="text-lg font-bold text-green-600">{patientData.images.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <Brain className="w-6 h-6 text-indigo-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Análises IA</p>
                <p className="text-lg font-bold text-indigo-600">{patientData.analyses.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <Receipt className="w-6 h-6 text-yellow-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Faturas</p>
                <p className="text-lg font-bold text-yellow-600">{patientData.invoices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="flex items-center">
              <DollarSign className="w-6 h-6 text-green-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Total Pago</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(getTotalPaidAmount())}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-6 px-6 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Informações Pessoais */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Informações Pessoais
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-500 mr-3" />
                        <div>
                          <span className="text-sm text-gray-600">Data de Nascimento:</span>
                          <span className="ml-2 text-gray-900">{formatDate(patient.birth_date)}</span>
                        </div>
                      </div>
                      {patient.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-500 mr-3" />
                          <div>
                            <span className="text-sm text-gray-600">Email:</span>
                            <span className="ml-2 text-gray-900">{patient.email}</span>
                          </div>
                        </div>
                      )}
                      {patient.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-500 mr-3" />
                          <div>
                            <span className="text-sm text-gray-600">Telefone:</span>
                            <span className="ml-2 text-gray-900">{patient.phone}</span>
                          </div>
                        </div>
                      )}
                      {patient.address && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-500 mr-3" />
                          <div>
                            <span className="text-sm text-gray-600">Endereço:</span>
                            <span className="ml-2 text-gray-900">{patient.address}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resumo Financeiro */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Resumo Financeiro
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total de Tratamentos:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(getTotalTreatmentCost())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Faturado:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(getTotalInvoiceAmount())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Pago:</span>
                        <span className="font-medium text-green-600">{formatCurrency(getTotalPaidAmount())}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-gray-600">Saldo Pendente:</span>
                        <span className="font-medium text-red-600">
                          {formatCurrency(getTotalInvoiceAmount() - getTotalPaidAmount())}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Histórico Médico */}
                {patient.medical_history_summary && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2" />
                      Histórico Médico
                    </h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{patient.medical_history_summary}</p>
                  </div>
                )}

                {/* Últimas Atividades */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Últimas Atividades
                  </h3>
                  <div className="space-y-3">
                    {/* Últimos tratamentos */}
                    {patientData.treatments.slice(-3).map(treatment => (
                      <div key={treatment.id} className="flex items-center">
                        <Activity className="w-4 h-4 text-purple-600 mr-3" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{treatment.procedure}</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(treatment.created_at)} - {treatment.status}
                          </p>
                        </div>
                      </div>
                    ))}
                    {/* Últimos agendamentos */}
                    {patientData.appointments.slice(-2).map(appointment => (
                      <div key={appointment.id} className="flex items-center">
                        <Calendar className="w-4 h-4 text-blue-600 mr-3" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{appointment.procedure}</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(appointment.appointment_date)} - {appointment.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'treatments' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Evolução Clínica e Tratamentos</h3>
                  <button
                    onClick={() => navigate('/tratamentos')}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center shadow-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Registro
                  </button>
                </div>
                {getEvolutionFeed().length === 0 ? (
                  <p className="text-gray-500 text-center py-12 bg-gray-50 rounded-2xl border border-dashed">Nenhum registro de evolução ou tratamento disponível.</p>
                ) : (
                  <div className="relative py-4 before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                    {getEvolutionFeed().map(item => {
                      const Icon = item.icon;
                      return (
                        <div key={item.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group mb-8">
                          {/* Marker */}
                          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-white ${item.iconBg} shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 bg-white z-10`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          
                          {/* Card */}
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-center mb-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(item.status)}`}>
                                {item.status}
                              </span>
                              <span className="text-xs text-gray-500 font-medium">
                                {formatDate(item.date)}
                              </span>
                            </div>
                            <h4 className="font-bold text-gray-900 text-lg mb-1">{item.title}</h4>
                            {item.description && <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>}
                            {item.cost && <div className="mt-3 text-sm font-semibold text-green-600 bg-green-50 px-3 py-1.5 rounded-lg inline-block">{formatCurrency(item.cost)}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'appointments' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Agendamentos</h3>
                  <button
                    onClick={() => navigate('/agenda')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Agendamento
                  </button>
                </div>
                {patientData.appointments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhum agendamento registrado</p>
                ) : (
                  <div className="space-y-4">
                    {patientData.appointments.map(appointment => (
                      <div key={appointment.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{appointment.procedure}</h4>
                            <div className="flex items-center mt-2 space-x-4">
                              <span className="text-sm text-gray-600">
                                {formatDate(appointment.appointment_date)} - {appointment.start_time}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                                {appointment.status}
                              </span>
                            </div>
                            {appointment.notes && (
                              <p className="text-sm text-gray-600 mt-1">{appointment.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'images' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Imagens e Documentos</h3>
                  <button
                    onClick={() => navigate('/imagens')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Imagem
                  </button>
                </div>
                {patientData.images.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma imagem disponível</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {patientData.images.map(image => (
                      <div key={image.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-600 capitalize">
                            {image.image_type?.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-gray-500">{formatDate(image.created_at)}</span>
                        </div>
                        
                        <div className="mb-3">
                          {image.mime_type?.startsWith('image/') ? (
                            <div 
                              className="h-32 bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90"
                              onClick={() => viewImage(image)}
                            >
                              <div className="h-full flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                                <p className="text-sm text-gray-500 ml-2">Clique para ver</p>
                              </div>
                            </div>
                          ) : (
                            <div 
                              className="h-32 bg-red-50 rounded-lg flex items-center justify-center cursor-pointer hover:bg-red-100"
                              onClick={() => viewImage(image)}
                            >
                              <div className="text-center">
                                <FileText className="w-12 h-12 text-red-600 mx-auto mb-2" />
                                <p className="text-sm text-red-600">Documento PDF</p>
                              </div>
                            </div>
                          )}
                        </div>

                        <p className="text-sm text-gray-900 mb-2 font-medium truncate">
                          {image.original_filename}
                        </p>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => viewImage(image)}
                            className="flex-1 bg-blue-50 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-100 flex items-center justify-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Ver
                          </button>
                          {image.mime_type?.startsWith('image/') && (
                            <button
                              onClick={() => downloadImage(image)}
                              className="bg-green-50 text-green-600 px-3 py-1 rounded text-xs hover:bg-green-100 flex items-center justify-center"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'anamnese' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Anamneses</h3>
                  <button
                    onClick={() => navigate('/anamnese')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Anamnese
                  </button>
                </div>
                {patientData.anamnese.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma anamnese disponível</p>
                ) : (
                  <div className="space-y-4">
                    {patientData.anamnese.map(anamnese => (
                      <div key={anamnese.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">{anamnese.template_title}</h4>
                          <span className="text-sm text-gray-500">{formatDate(anamnese.submitted_at)}</span>
                        </div>
                        {anamnese.answers && anamnese.answers.length > 0 && (
                          <div className="space-y-2">
                            {anamnese.answers.slice(0, 3).map((answer, index) => (
                              <div key={index} className="text-sm">
                                <span className="font-medium text-gray-700">{answer.question_text}: </span>
                                <span className="text-gray-600">{answer.answer}</span>
                              </div>
                            ))}
                            {anamnese.answers.length > 3 && (
                              <p className="text-sm text-gray-500">...e mais {anamnese.answers.length - 3} respostas</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'odontogram' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Odontograma</h3>
                  <button
                    onClick={() => {
                      localStorage.setItem('selectedPatientForOdontogram', JSON.stringify(patient));
                      navigate('/odontograma');
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir Odontograma
                  </button>
                </div>
                {patientData.odontogram ? (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="text-center mb-4">
                      <Smile className="w-16 h-16 text-purple-600 mx-auto mb-2" />
                      <p className="text-gray-600">Odontograma disponível</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div>
                        <span className="font-medium text-gray-700">Último exame: </span>
                        <span className="text-gray-600">
                          {patientData.odontogram.last_examination_date || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Próximo agendamento: </span>
                        <span className="text-gray-600">
                          {patientData.odontogram.next_appointment_date || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {patientData.odontogram.general_notes && (
                      <div className="mt-4">
                        <p className="font-medium text-gray-700 mb-2">Observações:</p>
                        <p className="text-gray-600">{patientData.odontogram.general_notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nenhum odontograma disponível</p>
                )}
              </div>
            )}

            {activeTab === 'analyses' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Análises de IA</h3>
                  <button
                    onClick={() => navigate('/ia')}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Análise
                  </button>
                </div>
                {patientData.analyses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma análise de IA disponível</p>
                ) : (
                  <div className="space-y-4">
                    {patientData.analyses.map(analysis => (
                      <div key={analysis.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <Brain className="w-5 h-5 text-purple-600 mr-2" />
                            <h4 className="font-medium text-gray-900">
                              Análise de {analysis.analysis_type}
                            </h4>
                          </div>
                          <span className="text-sm text-gray-500">{formatDate(analysis.created_at)}</span>
                        </div>
                        {analysis.results && analysis.results.summary && (
                          <div className="text-sm text-gray-700">
                            <p className="font-medium mb-1">Resumo:</p>
                            <p className="line-clamp-3">{analysis.results.summary}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'financial' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Financeiro</h3>
                  <button
                    onClick={() => navigate('/cobranca')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Fatura
                  </button>
                </div>

                {/* Resumo Financeiro */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Receipt className="w-6 h-6 text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm text-blue-600">Total Faturado</p>
                        <p className="text-lg font-bold text-blue-800">{formatCurrency(getTotalInvoiceAmount())}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm text-green-600">Total Pago</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(getTotalPaidAmount())}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
                      <div>
                        <p className="text-sm text-red-600">Saldo Devedor</p>
                        <p className="text-lg font-bold text-red-800">
                          {formatCurrency(getTotalInvoiceAmount() - getTotalPaidAmount())}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Faturas */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Faturas</h4>
                  {patientData.invoices.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nenhuma fatura emitida</p>
                  ) : (
                    <div className="space-y-3">
                      {patientData.invoices.map(invoice => (
                        <div key={invoice.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">Fatura #{invoice.invoice_number}</p>
                              <p className="text-sm text-gray-600">
                                Emitida em {formatDate(invoice.issue_date)}
                              </p>
                              {invoice.due_date && (
                                <p className="text-sm text-gray-600">
                                  Vencimento: {formatDate(invoice.due_date)}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">{formatCurrency(invoice.total_amount)}</p>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagamentos */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Pagamentos</h4>
                  {patientData.payments.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nenhum pagamento registrado</p>
                  ) : (
                    <div className="space-y-3">
                      {patientData.payments.map(payment => (
                        <div key={payment.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{payment.payment_method_name}</p>
                              <p className="text-sm text-gray-600">
                                {formatDate(payment.payment_date)}
                              </p>
                              {payment.reference && (
                                <p className="text-sm text-gray-600">Ref: {payment.reference}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Image Modal */}
        {showImageModal && selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedImage.original_filename}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadImage(selectedImage)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                {selectedImage.mime_type?.startsWith('image/') ? (
                  <img
                    src={`data:${selectedImage.mime_type};base64,${selectedImage.image_data}`}
                    alt={selectedImage.description || selectedImage.original_filename}
                    className="max-w-full h-auto mx-auto rounded-lg"
                  />
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-20 h-20 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg text-gray-700 mb-2">Documento PDF</p>
                    <p className="text-gray-500 mb-4">{selectedImage.original_filename}</p>
                    <button
                      onClick={() => downloadImage(selectedImage)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Baixar PDF
                    </button>
                  </div>
                )}
                
                {selectedImage.description && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-700 mb-2">Descrição:</p>
                    <p className="text-gray-600">{selectedImage.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PatientRecordPage;
